import React, { useEffect, useRef } from 'react';
import { useQuery } from '@apollo/client';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useAccount } from '../../context/AccountContext';
import { PageLoader } from '../common/PageLoader';
import { APPROVAL_KINDS, useApprovals } from './ApprovalsReview';
import { APPROVAL_SITE } from './approvalsGql';

/**
 * An agent's approval link (MCP approvalUrl → /approvals/:kind/:id, backlog
 * 8deccac5): Today underneath, the Approvals review open on that item. A link
 * for another of the person's sites shows the app as that site first; signed
 * out, sign-in brings them back here (postLoginRedirect).
 */
export function ApprovalsLink() {
  const { kind = '', id = '' } = useParams();
  const { accountId, selectAccountById } = useAccount();
  const { open } = useApprovals();
  const navigate = useNavigate();
  const known = APPROVAL_KINDS.includes(kind) && Boolean(id);
  // Support requests are read per site (the owner's); everything else says where it lives.
  const { data, loading, error } = useQuery<{ approvalSite: string | null }>(APPROVAL_SITE, {
    variables: { kind, id },
    skip: !known || kind === 'support',
    fetchPolicy: 'network-only',
  });
  const switched = useRef(false);

  useEffect(() => {
    if (!known || loading || !accountId) return;
    const site = error ? null : data?.approvalSite || null;
    if (site && site !== accountId && !switched.current) {
      switched.current = true;
      selectAccountById(site);
      return;
    }
    if (site && site !== accountId) return; // waiting for the switch to land
    navigate('/today', { replace: true });
    open({ kind, id });
  }, [known, loading, error, data, accountId, kind, id, navigate, open, selectAccountById]);

  if (!known) return <Navigate to="/today" replace />;
  return <PageLoader />;
}

/** The links agents sent before the review (/reach/approve/:id, /agents/approve/:id). */
export function OldApprovalLink({ kind }: { kind: 'post' | 'agent' }) {
  const params = useParams();
  const id = params.postId || params.id || '';
  return <Navigate to={`/approvals/${kind}/${id}`} replace />;
}
